import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { nx, glass, NX_RING_CLIP, type NxSize } from "../lib/onyx-material";

/** Rim color per glass variant — panel = `--glassBd`, composer = `--acLine`. */
const GLASS_RIM = {
  panel: "var(--glassBd)",
  composer: "var(--acLine)",
} as const;

/**
 * GlassSurface — frosted-glass elevation for FLOATING surfaces (the AppShell
 * Brain, the chat composer, ArtifactFocus). A translucent, blurred, blue-lumen
 * panel that lifts off a flush mood-lit base.
 *
 * THE LOAD-BEARING TWO-LAYER RULE: a floating glass panel is TWO stacked,
 * IDENTICALLY notch-clipped (`.nx-*`) layers inside a positioning wrapper,
 * because `backdrop-filter` and `filter` CANCEL when applied to the same node.
 * So they are split, and they must never be collapsed back onto one element:
 *   - BACKING layer (`.glass-back`, z-0): carries ONLY `filter: var(--dropFloat)`
 *     (the float drop-shadow — a `drop-shadow` survives `clip-path`, where an
 *     outer `box-shadow` is clipped away to nothing) over the `--glassBack`
 *     backstop fill. Empty + `aria-hidden`; it is purely a shadow caster.
 *   - GLASS layer (`.glass-panel` / `.glass-composer`, z-1): carries ONLY
 *     `backdrop-filter: blur(22px) saturate(150%)` over the `--glass` translucent
 *     fill, the `--glassSheen` top-right lumen, the `--glassHi` top highlight
 *     (an inset 1px sheen) and `children`.
 *   - RIM layer (`NxKeyline`, z-2): the continuous rim keyline as an un-clipped
 *     SVG stroke overlay — a border / inset box-shadow rim was sliced at the
 *     notch (issue #158). `variant="panel"` strokes `--glassBd`; `variant=
 *     "composer"` strokes the `--acLine` accent (the chat input bar).
 *
 * The SAME `nx` notch clips BOTH layers so the blur silhouette and the shadow
 * silhouette register exactly. The wrapper carries NEITHER filter and — load-
 * bearing for the frost — NO `isolation: isolate`. `backdrop-filter` only
 * samples pixels painted behind the element WITHIN the same backdrop-root; an
 * `isolate` wrapper walls the frost off from the page content behind the panel,
 * so the blur has nothing real to sample and reads as a flat fill (issue #159).
 * The wrapper is plain `position: relative` (the backing/glass z-order is
 * preserved by `z-0`/`z-[1]`, no stacking context needed) and owns
 * width/height/positioning via `wrapperClassName`.
 *
 * Floating glass panels do NOT take `.raise` (that is the base-button keycap);
 * the float here is `--dropFloat`, deeper than `--dropCard`. All colour/shadow
 * comes from tokens referenced BY NAME — zero raw hex in this component.
 *
 * Geometry is theme-agnostic; only the 7 glass token VALUES flip under `.dark`.
 * SSR-safe (no effects, no window access) and reduced-motion-safe (no animation
 * of its own; composes cleanly inside a parent that animates width, e.g. the
 * Brain).
 *
 * @example
 *   <GlassSurface variant="panel" nx="l" wrapperClassName="w-80" className="p-5">
 *     <h3>Brain</h3>
 *   </GlassSurface>
 *
 *   <GlassSurface as="form" variant="composer" nx="m" className="flex items-center gap-2 p-2">
 *     <input ... />
 *   </GlassSurface>
 */
export const glassSurfaceVariants = cva("", {
  variants: {
    variant: {
      panel: glass.panel,
      composer: glass.composer,
    },
  },
  defaultVariants: {
    variant: "panel",
  },
});

export interface GlassSurfaceProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "color">,
    VariantProps<typeof glassSurfaceVariants> {
  /**
   * Keyline + role. `"panel"` = floating frosted surface (Brain, ArtifactFocus)
   * with the `--glassBd` inset rim; `"composer"` = raised glass input bar with
   * the `--acLine` accent keyline. Default `"panel"`.
   */
  variant?: "panel" | "composer";
  /**
   * Notch-clip size, applied IDENTICALLY to BOTH layers so the silhouettes
   * register. Default `"l"` (panel/rail scale); pass `"m"` for the composer
   * field scale.
   */
  nx?: NxSize;
  /**
   * Polymorphic element for the positioning WRAPPER (the outer node only). The
   * two inner clipped layers are always `<div>`s. Mutually informs but is
   * independent of `asChild`. Default `"div"`.
   */
  as?: React.ElementType;
  /**
   * Radix Slot escape hatch on the WRAPPER — merges GlassSurface's wrapper
   * props/ref onto the single passed child (a motion element, a router node)
   * instead of rendering its own wrapper. The two clipped layers render as that
   * child's children (via `Slottable`), so the consumer owns content
   * composition. Mutually exclusive with a meaningful `as`. Default `false`.
   */
  asChild?: boolean;
  /**
   * Merged onto the GLASS (top) layer's content box — padding, layout
   * (flex/grid), min/max sizing, justify/align of the panel body.
   */
  className?: string;
  /**
   * Merged onto the positioning WRAPPER — width/height/flex-basis (the Brain
   * animates width here), absolute/fixed positioning, z-index, margins. Kept
   * separate because backdrop-filter and the drop-shadow live on the inner
   * layers, never the wrapper.
   */
  wrapperClassName?: string;
  /**
   * Override the backdrop blur radius in px. Escape hatch only; the canonical
   * 22px is the default and should rarely change.
   */
  blur?: number;
  /** Panel/composer content — rendered inside the GLASS (top) layer only. */
  children?: React.ReactNode;
}

const DEFAULT_BLUR = 22;

export const GlassSurface = React.forwardRef<HTMLElement, GlassSurfaceProps>(
  (
    {
      variant = "panel",
      nx: nxSize = "l",
      as,
      asChild = false,
      className,
      wrapperClassName,
      blur,
      children,
      ...rest
    },
    ref,
  ) => {
    const notch = nx[nxSize];

    // Escape hatch only: a non-default blur overrides the class recipe's 22px on
    // the glass layer's content box. The default rides `.glass-panel`/
    // `.glass-composer` so no per-instance style object is created in the common
    // case. saturate(150%) is preserved alongside the override.
    const blurStyle =
      blur != null && blur !== DEFAULT_BLUR
        ? ({
            backdropFilter: `blur(${blur}px) saturate(150%)`,
            WebkitBackdropFilter: `blur(${blur}px) saturate(150%)`,
          } as React.CSSProperties)
        : undefined;

    // BACKING layer: same notch, fills the wrapper, z-0. ONLY the float
    // drop-shadow + the backstop fill — never backdrop-filter. Empty.
    const backing = (
      <div aria-hidden className={cn(notch, glass.back, "absolute inset-0 z-0")} />
    );
    // GLASS layer: same notch, z-1. ONLY backdrop-filter (the frost) + the sheen
    // + the --glassHi top highlight. In the default path children paint above the
    // frost here; in `asChild` the consumer owns content composition, so the
    // glass layer renders empty (the layers become the consumer element's
    // children via Slottable).
    const glassLayer = (
      <div
        className={cn(notch, glassSurfaceVariants({ variant }), "relative z-[1]", className)}
        style={blurStyle}
      >
        {asChild ? null : children}
      </div>
    );
    // RIM layer: a hollow 1px frame on the SAME `nx` clip (`.nx-rim`). The
    // clip-path gives the notched outer edge and the mask hollows the interior,
    // so the rim traces the full silhouette INCLUDING the notch in pure CSS — no
    // measurement. This replaces the measured `NxKeyline` SVG overlay, whose
    // path was computed for a STALE width during the Brain's width animation and
    // left a residue triangle at the notch. The frame's interior is transparent
    // and it sits ABOVE the glass, so it never blurs into the frost (issue #159).
    // TODO(aisquare-ui#168): the glass panel edges still need polish (user
    // feedback). Kept working here (ArtifactTile / RailNav depend on the ring);
    // a follow-up PR refines the glass-variant edge treatment.
    const rim = (
      <span
        aria-hidden
        className="nx-rim absolute inset-0 z-[2] pointer-events-none"
        style={
          { "--nx-rim": GLASS_RIM[variant], clipPath: NX_RING_CLIP[nxSize] } as React.CSSProperties
        }
      />
    );

    // NO `isolate` here: `isolation: isolate` would make the wrapper its own
    // stacking context and wall the backdrop-filter off from the page behind the
    // panel, so the frost would sample only the backing fill and read flat
    // (issue #159). Plain `relative` keeps the backing (z-0) below and the glass
    // (z-[1]) above without creating a backdrop-root boundary.
    const wrapperClass = cn("relative", wrapperClassName);

    // asChild: merge the wrapper props/ref onto the single passed child (a motion
    // element, a router node). `Slottable` marks that child as the merge target
    // so the clipped layers render as ITS children — backdrop-filter and the
    // drop-shadow stay on the inner layers, never the merged wrapper.
    if (asChild) {
      return (
        <Slot ref={ref} className={wrapperClass} {...rest}>
          <Slottable>{children}</Slottable>
          {backing}
          {glassLayer}
          {rim}
        </Slot>
      );
    }

    // The wrapper: position:relative only (NO isolate — see wrapperClass note;
    // the backing shadow paints below and children above the frost via z-order).
    // Carries NO filter and NO backdrop-filter. Polymorphic via `as`.
    const Wrapper: React.ElementType = as ?? "div";
    return (
      <Wrapper ref={ref} className={wrapperClass} {...rest}>
        {backing}
        {glassLayer}
        {rim}
      </Wrapper>
    );
  },
);

GlassSurface.displayName = "GlassSurface";
